import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  MemberRosterEntry,
  MemberRosterQueryParams,
  MemberBoat,
  MemberBoatQueryParams,
} from "@/types/members-roster.types";

export const ClubMembersRosterService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllMemberRosters: builder.query<
      PaginatedApiResponse<MemberRosterEntry[]>,
      MemberRosterQueryParams
    >({
      query: (params) => ({
        url: "club-member-roster",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ClubsMembers" as const,
                id,
              })),
              { type: "ClubsMembers", id: "LIST" },
            ]
          : [{ type: "ClubsMembers", id: "LIST" }],
      keepUnusedDataFor: 0, // Add this line to disable caching
    }),

    getMemberRosterById: builder.query<MemberRosterEntry, string>({
      query: (id) => ({
        url: `club-member-roster/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "ClubsMembers", id }],
      transformResponse: (
        response: ApiResponse<MemberRosterEntry> | MemberRosterEntry,
      ) => {
        if ("data" in response && "status" in response) {
          return response.data;
        }
        return response as MemberRosterEntry;
      },
    }),

    createMemberRoster: builder.mutation<
      ApiResponse<MemberRosterEntry>,
      Partial<MemberRosterEntry>
    >({
      query: (data) => ({
        url: "club-member-roster",
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "ClubsMembers", id: "LIST" }],
    }),

    updateMemberRoster: builder.mutation<
      ApiResponse<MemberRosterEntry>,
      { id: string; data: Partial<MemberRosterEntry> }
    >({
      query: ({ id, data }) => ({
        url: `club-member-roster/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ClubsMembers", id },
        { type: "ClubsMembers", id: "LIST" },
      ],
    }),

    updateMemberRosterStatus: builder.mutation<
      ApiResponse<void>,
      { id: string; status: number }
    >({
      query: ({ id, status }) => ({
        url: `club-member-roster/${id}/status`,
        method: "PATCH",
        data: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ClubsMembers", id },
        { type: "ClubsMembers", id: "LIST" },
      ],
    }),

    deleteMemberRoster: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `club-member-roster/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "ClubsMembers", id },
        { type: "ClubsMembers", id: "LIST" },
      ],
    }),

    getLinkedBoats: builder.query<
      PaginatedApiResponse<MemberBoat[]>,
      { id: string; params?: MemberBoatQueryParams }
    >({
      query: ({ id, params }) => ({
        url: `club-member-roster/${id}/boats/linked`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, { id }) => [
        { type: "ClubsMembers", id: `LINKED_BOATS_${id}` },
        ...(result
          ? result.data.map((boat) => ({ type: "Boats" as const, id: boat.id }))
          : []),
      ],
      keepUnusedDataFor: 0,
      transformResponse: (
        response:
          | ApiResponse<PaginatedApiResponse<MemberBoat[]>>
          | PaginatedApiResponse<MemberBoat[]>,
      ) => {
        if ("data" in response && "status" in response) {
          return response.data;
        }
        return response as PaginatedApiResponse<MemberBoat[]>;
      },
    }),

    getUnlinkedBoats: builder.query<
      PaginatedApiResponse<MemberBoat[]>,
      { id: string; params?: MemberBoatQueryParams }
    >({
      query: ({ id, params }) => ({
        url: `club-member-roster/${id}/boats/unlinked`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, { id }) => [
        { type: "ClubsMembers", id: `UNLINKED_BOATS_${id}` },
        ...(result
          ? result.data.map((boat) => ({ type: "Boats" as const, id: boat.id }))
          : []),
      ],
      keepUnusedDataFor: 0,
      transformResponse: (
        response:
          | ApiResponse<PaginatedApiResponse<MemberBoat[]>>
          | PaginatedApiResponse<MemberBoat[]>,
      ) => {
        if ("data" in response && "status" in response) {
          return response.data;
        }
        return response as PaginatedApiResponse<MemberBoat[]>;
      },
    }),

    linkBoat: builder.mutation<
      ApiResponse<void>,
      { id: string; boatId: string }
    >({
      query: ({ id, boatId }) => ({
        url: `club-member-roster/${id}/boats/${boatId}/link`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id, boatId }) => [
        { type: "ClubsMembers", id },
        { type: "ClubsMembers", id: "LIST" },
        { type: "ClubsMembers", id: `LINKED_BOATS_${id}` },
        { type: "ClubsMembers", id: `UNLINKED_BOATS_${id}` },
        { type: "Boats", id: boatId },
      ],
    }),

    unlinkBoat: builder.mutation<
      ApiResponse<void>,
      { id: string; boatId: string }
    >({
      query: ({ id, boatId }) => ({
        url: `club-member-roster/${id}/boats/${boatId}/unlink`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id, boatId }) => [
        { type: "ClubsMembers", id },
        { type: "ClubsMembers", id: "LIST" },
        { type: "ClubsMembers", id: `LINKED_BOATS_${id}` },
        { type: "ClubsMembers", id: `UNLINKED_BOATS_${id}` },
        { type: "Boats", id: boatId },
      ],
    }),
  }),
});

export const {
  useGetAllMemberRostersQuery,
  useGetMemberRosterByIdQuery,
  useCreateMemberRosterMutation,
  useUpdateMemberRosterMutation,
  useDeleteMemberRosterMutation,
  useUpdateMemberRosterStatusMutation,
  useGetLinkedBoatsQuery,
  useGetUnlinkedBoatsQuery,
  useLinkBoatMutation,
  useUnlinkBoatMutation,
} = ClubMembersRosterService;
